import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";

//graphql
import { useQuery, useMutation, ApolloError } from "@apollo/client";
import { GET_DEVICE, POST_COUNT, UPDATE_SUMMARY } from "../queries";

//redux
import { useAppDispatch, useAppSelector } from "../hooks/reduxHooks";
import { stopAlarm, updatePageButtons, updatePageTitle } from "../store/layout";
import { startAlarm } from "../store/layout";

//material-ui
import { useConfirm } from "material-ui-confirm";
import {
	Alert,
	Button,
	Chip,
	Grid,
	TextField,
	Typography,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";

//hooks/providers
import { useSnacks } from "../providers/SnackProvider";
import { MqttContext } from "../providers/MqttProvider";
import useInterval from "../hooks/useInterval";

//components
import JobDetail from "./components/JobDetail";
import AllottedTime from "./components/AllottedTime";
import CountHistoryChart from "./components/CountHistoryChart";
import ScreenPaper from "./components/ScreenPaper";
import SelectLocation from "./components/SelectLocation";
import EmployeeCount from "./components/EmployeeCount";
import TimeRemainingFormatted from "./components/TimeRemainingFormatted";
import SummaryUpdateSubscription from "./components/SummaryUpdateSubscription";
import WatchForNewJob from "./components/WatchForNewJob";

//helpers
import { timestampFormatDateTime, timestampFormatTime } from "../helpers/timestampFormat";
import HandleApolloError from "../helpers/HandleApolloError";
import { updateCurrentAlarmEnabled, updateCurrentAlarmThreshold } from "../store/settings";

const CounterScreen = (props: any) => {
	const confirm = useConfirm();
	const [openSnack] = useSnacks();
	const { mqttPublish, mqttStatus, mqttStatusType } = useContext(MqttContext);

	const [Device, setDevice] = useState<Device | null>(null);
	const [JobSummary, setJobSummary] = useState<JobSummary | null>(null);
	let { id } = useParams();

	const navigate = useNavigate();

	const dispatch = useAppDispatch();

	const FromDevice = useAppSelector((state) => state.device[Number(id)]);
	const DeviceCommand = useAppSelector((state) => state.deviceCommand[Number(id)]);
	const playAlarm = useAppSelector((state) => state.layout.playAlarm);
	const alarmEnabled = useAppSelector((state) => state.settings.alarmEnabled);
	const alarmThreshold = useAppSelector((state) => state.settings.alarmThreshold);
	const currentAlarmEnabled = useAppSelector((state) => state.settings.currentAlarmEnabled);
	const currentAlarmThreshold = useAppSelector((state) => state.settings.currentAlarmThreshold);

	const [location, setLocation] = useState<number | null>(null);
	const [employeeCount, setEmployeeCount] = useState<number>(0);

	const [allottedMinutes, setAllottedMinutes] = useState<number>(0);
	const [note, setNote] = useState("");
	const [newcount, setNewCount] = useState<number | string>("");
	const [newTarget, setNewTarget] = useState<number | string>("");
	const [targetOverride, setTargetOverride] = useState<number>(0);
	const [allottedOpen, setAllottedOpen] = useState(false);
	const [timeCheck, setTimeCheck] = useState(new Date().getTime() / 1000 - 120);
	const [alarmStarted, setAlarmStarted] = useState(false);

	const dataLoaded = (d: { countcDevice: Device; countcCountSummary: JobSummary }) => {
		console.log("dataLoaded", d);
		dispatch(updatePageTitle("Count Screen: " + d.countcDevice?.Title));
		setDevice(d.countcDevice);
		setJobSummary(d.countcCountSummary);
		setLocation(d.countcCountSummary.LocationID);
		setAllottedMinutes(d.countcCountSummary.AllottedMinutes || 120);
		setEmployeeCount(d.countcCountSummary.People || 1);
		setTargetOverride(d.countcCountSummary.Target || 0);
	};

	const updateCompleted = (d: { countcUpdateSummary: JobSummary }) => {
		setLocation(d.countcUpdateSummary.LocationID);
		setAllottedMinutes(d.countcUpdateSummary.AllottedMinutes || 120);
		setEmployeeCount(d.countcUpdateSummary.People || 1);
		setTargetOverride(d.countcUpdateSummary.Target || 0);
	};

	const { loading, error, refetch } = useQuery(GET_DEVICE, {
		variables: { DeviceID: Number(id) },
		onCompleted: dataLoaded,
		notifyOnNetworkStatusChange: true,
		//pollInterval: 30000,
	});

	useInterval(() => {
		setTimeCheck(new Date().getTime() / 1000);
	}, 10000);

	const postCompleted = async (d: any) => {
		console.log("postCompleted", d);
		openSnack("Count posted", "success");
		let topic = "/countc/" + id + "/command";
		mqttPublish(topic, '{"cmdType":"resetCount"}', { retain: false });
		setNote("");
		setLocation(null);
		setTargetOverride(0);
		setNewTarget("");
	};

	const handleLocationChange = (value: number | null) => {
		setLocation(value);
		updateSummary({
			variables: {
				input: { DeviceID: Number(id), LocationID: value },
			},
		});
	};

	const handleEmployeeCountChange = (value: number) => {
		console.log("handleEmployeeCountChange", value);
		updateSummary({
			variables: {
				input: { DeviceID: Number(id), People: value },
			},
		});
	};

	const handleAllottedMinutesChange = (value: number) => {
		setAllottedMinutes(value);
		updateSummary({
			variables: {
				input: { DeviceID: Number(id), AllottedMinutes: value },
			},
		});
	};

	const handleTargetChange = () => {
		setTargetOverride(Number(newTarget));
		setNewTarget("");
		updateSummary({
			variables: {
				input: { DeviceID: Number(id), Target: Number(newTarget) },
			},
		});
	};

	const postError = (error: ApolloError) => {
		let errs = HandleApolloError("Posting Count", error);
		for (let err of errs) {
			openSnack(err, "error", true);
		}
	};

	const [updateSummary] = useMutation(UPDATE_SUMMARY, {
		onCompleted: (d) => {
			updateCompleted(d);
		},
		onError: (e) => {
			console.log(e);
			refetch();
		},
	});

	const [postCnt] = useMutation(POST_COUNT, {
		onCompleted: postCompleted,
		onError: postError,
		refetchQueries: ["GetJobs", "GetDevice"],
	});

	const postCount = () => {
		confirm({
			title: "Are you sure you want to post the current count?",
			cancellationButtonProps: { variant: "outlined" },
			confirmationButtonProps: { variant: "outlined" },
		})
			.then(() => {
				postCnt({
					variables: {
						input: { DeviceID: Number(id), Quantity: FromDevice.Quantity, Note: note || null, LocationID: location, Posted: 1, People: employeeCount },
					},
				});
			})
			.catch(() => {});
	};

	const cancelCount = () => {
		confirm({
			title: "Are you sure you want to cancel the current count? Current progress will be lost.",
			confirmationText: "Cancel This Count",
			cancellationText: "Do Not Cancel",
			cancellationButtonProps: { variant: "outlined" },
			confirmationButtonProps: { variant: "outlined", color: "error" },
		})
			.then(() => {
				postCnt({
					variables: {
						input: { DeviceID: Number(id), Quantity: 0, Note: "Cancelled", LocationID: location, Posted: 1, People: employeeCount },
					},
				});
			})
			.catch(() => {});
	};

	const changeCount = async () => {
		if (newcount === null) {
			openSnack("Enter a number", "error");
			return;
		}
		confirm({
			title: "Are you sure you want to set the current count to " + newcount + "?",
			cancellationButtonProps: { variant: "outlined" },
			confirmationButtonProps: { variant: "outlined" },
		})
			.then(() => {
				let topic = "/countc/" + id + "/command";
				console.log(topic);
				mqttPublish(topic, '{"cmdType":"setCount", "count": ' + newcount + "}", { retain: false });
				setNewCount("");
			})
			.catch(() => {});
	};

	function newCountChange(event: any) {
		console.log(typeof event.target.value);
		if (event.target.value === "") {
			setNewCount("");
		} else if (newcount !== Number(event.target.value)) {
			setNewCount(Number(event.target.value));
		}
	}

	function newTargetChange(event: any) {
		console.log(typeof event.target.value);
		if (event.target.value === "") {
			setNewTarget("");
		} else if (newcount !== Number(event.target.value)) {
			setNewTarget(Number(event.target.value));
		}
	}
	useEffect(() => {
		setAlarmStarted(false);
		dispatch(updateCurrentAlarmEnabled(alarmEnabled));
		dispatch(updateCurrentAlarmThreshold(alarmThreshold));
		return () => {
			setAlarmStarted(false);
			dispatch(stopAlarm());
			dispatch(updateCurrentAlarmEnabled(alarmEnabled));
			dispatch(updateCurrentAlarmThreshold(alarmThreshold));
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setAlarmStarted(false);
	}, [currentAlarmEnabled, currentAlarmThreshold]);

	useEffect(() => {
		dispatch(updatePageButtons([]));
	}, [dispatch]);

	if (error) return <Alert severity="info">{error.message}</Alert>;

	if (loading && !Device) return <Alert severity="info">Loading...</Alert>;

	if (!Device && !loading) return <Alert severity="info">Device not found</Alert>;

	let target = targetOverride || Number(JobSummary?.TargetQuantity) - Number(JobSummary?.CompletedQuantity);
	let elapsedMinutes = (timeCheck - Number(JobSummary?.CreateDate) / 1000) / 60;
	let allottedFormatted = Math.floor(allottedMinutes / 60) + ":" + ("00" + (allottedMinutes % 60)).slice(-2);
	let remainingMinutes = allottedMinutes - elapsedMinutes;
	let remaining = target - Number(FromDevice?.Quantity);
	let requiredRate: number;
	let requiredRateFormatted;
	let waitForData = 0.2;

	if (elapsedMinutes < waitForData) {
		requiredRate = 0;
		requiredRateFormatted = "...";
	} else if (remaining <= 0) {
		requiredRate = 0;
		requiredRateFormatted = <Chip size="small" variant="outlined" color="success" label="DONE" />;
	} else if (remainingMinutes > 0) {
		requiredRate = remaining / remainingMinutes;
		requiredRateFormatted = (remaining / remainingMinutes).toFixed(1) + "/min";
	} else {
		requiredRate = remaining;
		requiredRateFormatted = <Chip size="small" variant="outlined" color="error" label="LATE" />;
	}
	let averageRate = elapsedMinutes > 0 ? Number(FromDevice?.Quantity) / elapsedMinutes : 0;
	let averageRateFormatted: string | JSX.Element;
	if (elapsedMinutes < waitForData) {
		averageRateFormatted = <Chip variant="filled" color="default" label="Waiting..." />;
	} else if (averageRate > requiredRate * 1.05) {
		averageRateFormatted = <Chip variant="filled" color="success" label={averageRate.toFixed(1) + "/min"} />;
	} else if (averageRate < requiredRate * 0.95) {
		averageRateFormatted = <Chip variant="filled" color="error" label={averageRate.toFixed(1) + "/min"} />;
	} else {
		averageRateFormatted = <Chip variant="filled" color="info" label={averageRate.toFixed(1) + "/min"} />;
	}
	let calcFinishMinutes: number = 0;
	if (remaining && elapsedMinutes > waitForData) {
		if (averageRate) {
			calcFinishMinutes = Math.floor(remaining / averageRate);
		}
	}

	const alartTarget = (target * currentAlarmThreshold) / 100;
	const alarmState = Number(FromDevice?.Quantity) > alartTarget;

	if (JobSummary?.JobID && !JobSummary.Posted && alarmState) {
		if (!alarmStarted && currentAlarmEnabled) {
			dispatch(startAlarm());
			setAlarmStarted(true);
		}
	}

	return (
		<>
			<Grid container spacing="1vh" alignItems="stretch" sx={{ backgroundColor: playAlarm ? "red" : "" }}>
				<Grid item xs={12} sm={12} md={5}>
					{JobSummary?.JobID && !JobSummary.Posted ? (
						<Grid container spacing="1vh" alignItems="stretch">
							<Grid item xs={12}>
								<ScreenPaper sx={{ overflow: "auto" }}>
									<JobDetail JobSummary={JobSummary} cancelCount={cancelCount} />
								</ScreenPaper>
							</Grid>
							<Grid item xs={12}>
								<ScreenPaper>
									<Typography variant="h5">Number of Employees</Typography>
									<EmployeeCount employeeCount={employeeCount} setEmployeeCount={setEmployeeCount} handleEmployeeCountChange={handleEmployeeCountChange} />
								</ScreenPaper>
							</Grid>
							<Grid item xs={12}>
								<TableContainer component={ScreenPaper}>
									<Table aria-label="simple table" size="small">
										<TableHead>
											<TableRow>
												<TableCell>Started</TableCell>
												<TableCell>Allotted Time</TableCell>
												<TableCell>Time Remaining</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											<TableRow>
												<TableCell> {timestampFormatTime(Number(JobSummary.CreateDate))}</TableCell>
												<TableCell>
													{" "}
													<Button
														variant="outlined"
														onClick={() => {
															setAllottedOpen(true);
														}}
														startIcon={<SettingsIcon />}
													>
														{allottedFormatted}
													</Button>
												</TableCell>
												<TableCell className={remainingMinutes / (allottedMinutes || 1) <= 0.05 ? "endOfTime" : ""}>
													<TimeRemainingFormatted remainingMinutes={remainingMinutes} />
												</TableCell>
											</TableRow>
										</TableBody>
										<TableHead>
											<TableRow>
												<TableCell>Required Rate</TableCell>
												<TableCell>Average Rate</TableCell>
												<TableCell>Est. Time To Done</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											<TableRow>
												<TableCell>{requiredRateFormatted}</TableCell>
												<TableCell>{averageRateFormatted}</TableCell>
												<TableCell className={calcFinishMinutes < 0 ? "overcount" : ""}>
													{calcFinishMinutes ? <TimeRemainingFormatted remainingMinutes={calcFinishMinutes} /> : "--"}
												</TableCell>
											</TableRow>
										</TableBody>
									</Table>
								</TableContainer>
							</Grid>
							<Grid item xs={12}>
								<ScreenPaper>
									<Grid container spacing="1vh" alignItems="stretch">
										<Grid item xs={12}>
											<SelectLocation location={location} handleLocationChange={handleLocationChange} />
										</Grid>
										<Grid item xs={12}>
											<TextField label="Note" type="text" fullWidth value={note} onChange={(e) => setNote(e.target.value)}></TextField>
										</Grid>
										<Grid item xs={12} textAlign="center">
											<Button onClick={postCount} variant="contained" color="primary" fullWidth>
												Post Count
											</Button>
										</Grid>
									</Grid>
								</ScreenPaper>
							</Grid>
						</Grid>
					) : (
						<Grid container spacing="1vh" alignItems="stretch">
							<Grid item xs={12} textAlign="center">
								<ScreenPaper>
									<Grid container spacing="1vh" alignItems="stretch">
										<Grid item xs={12} textAlign="center">
											<Button onClick={() => navigate("/jobs/" + id)} variant="contained" color="primary" fullWidth>
												Select Job
											</Button>
											<WatchForNewJob dataLoaded={dataLoaded} DeviceCommand={DeviceCommand} />
										</Grid>
									</Grid>
								</ScreenPaper>
							</Grid>
						</Grid>
					)}
				</Grid>
				<Grid item xs={12} sm={12} md={7}>
					<Grid container spacing="1vh" alignItems="stretch">
						{JobSummary?.JobID && !JobSummary.Posted ? (
							<Grid item xs={12}>
								<ScreenPaper>
									<Grid container>
										<Grid item xs>
											<Typography variant="h5">Target </Typography>
										</Grid>
										<Grid item>
											<TextField
												label=""
												size="small"
												type="number"
												variant="outlined"
												value={newTarget}
												onChange={(e) => newTargetChange(e)}
												onKeyDown={(event) => {
													if (event.key === "Enter") {
														handleTargetChange();
														if (event.nativeEvent.target) {
															(event.nativeEvent.target as HTMLElement).blur();
														}
													}
												}}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<Button aria-label="Target" color="secondary" variant="contained" onClick={handleTargetChange} size="small">
																Set Target
															</Button>
														</InputAdornment>
													),
												}}
											></TextField>
										</Grid>
									</Grid>
									<div className="countbox">{loading && !Device ? "?" : JobSummary.JobID ? <> {target} </> : <>-</>}</div>
								</ScreenPaper>
							</Grid>
						) : null}
						<Grid item xs={12}>
							<ScreenPaper>
								<Typography variant="h5">Count</Typography>
								<div className={target && Number(FromDevice?.Quantity) > target ? "countbox overcount" : "countbox"}>{FromDevice?.Quantity}</div>
							</ScreenPaper>
						</Grid>
						<Grid item xs={12}>
							<ScreenPaper>
								{" "}
								<Grid container spacing={2}>
									<Grid item xs={12} md={6}>
										Last Update:{" "}
										<Chip
											color={Number(FromDevice?.DeviceTime) < timeCheck - 120 ? "error" : "default"}
											size="small"
											variant="outlined"
											label={timestampFormatDateTime(Number(FromDevice?.DeviceTime), 1000)}
										/>
										<br />
										Connection Status: <Chip size="small" variant="outlined" color={mqttStatusType} label={mqttStatus || "?"} />
									</Grid>
									<Grid item xs={12} md={6} textAlign="right">
										<TextField
											label=""
											size="small"
											type="number"
											variant="outlined"
											value={newcount}
											onChange={(e) => newCountChange(e)}
											onKeyDown={(event) => {
												if (event.key === "Enter") {
													changeCount();
													if (event.nativeEvent.target) {
														(event.nativeEvent.target as HTMLElement).blur();
													}
												}
											}}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<Button aria-label="Add" color="secondary" variant="contained" onClick={changeCount} size="small">
															Set Count
														</Button>
													</InputAdornment>
												),
											}}
										></TextField>
									</Grid>
									{JobSummary?.JobID && !JobSummary.Posted ? (
										<Grid item xs={12}>
											<CountHistoryChart
												DeviceID={Number(id)}
												CountSummaryID={JobSummary.CountSummaryID}
												StaticRate={Number(target / allottedMinutes).toFixed(1)}
												pollingInterval={Number(Device?.UpdateInterval) || 300}
											/>
										</Grid>
									) : null}
								</Grid>
							</ScreenPaper>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<AllottedTime
				allottedMinutes={allottedMinutes}
				setAllottedMinutes={setAllottedMinutes}
				allottedOpen={allottedOpen}
				setAllottedOpen={setAllottedOpen}
				handleAllottedMinutesChange={handleAllottedMinutesChange}
			/>
			<SummaryUpdateSubscription CountSummaryID={JobSummary?.CountSummaryID || 0} JobID={JobSummary?.JobID || 0} />
		</>
	);
};

//<ChangedCount DeviceId={dev.id}></ChangedCount>
//StaticRate={Math.floor(Number(((target / allottedMinutes) * Number(Device.UpdateInterval)) / 60))}

export default CounterScreen;
